import { Input, TextArea } from '../../../../shared/components/FormControls';
import { useState, useRef } from 'react';
import Rodal from 'rodal';
import { Button } from '../../../../shared/components/Button';
import '../../../../shared/styles/Orchestrator.css';
import 'rodal/lib/rodal.css';
import {
  BookOpen,
  BookPlus,
  Eye,
  EyeOff,
  Trash2,
  Upload,
  X,
  ChevronLeft,
  FileText,
  Share2,
} from 'lucide-react';

/**
 * LibraryModal – in-game library.
 *
 * GM mode (isGm=true): add/edit/delete books, toggle visibility.
 * Player mode (isGm=false): view visible books only.
 *
 * Props:
 *   visible    – boolean
 *   library    – { books: [{ id, title, coverUrl, content, pdfUrl, hidden }] }
 *   isGm       – boolean
 *   mode       – 'light' | 'dark'
 *   onClose    – () => void
 *   onSave     – (library) => void  (GM only)
 *   isLoading  – boolean
 */
export default function LibraryModal({
  visible,
  library,
  isGm = false,
  mode = 'light',
  onClose,
  onSave,
  playerShareActive = false,
  onTogglePlayerShare,
  isLoading = false,
  initialBookId = null,
}) {
  const isDark = mode === 'dark';
  const books = library?.books || [];

  // null = list view, string id = viewing book, 'editing' = adding/editing
  const [view, setView] = useState(() => initialBookId || null);
  const [editingBook, setEditingBook] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const coverInputRef = useRef(null);
  const pdfInputRef = useRef(null);

  const openBook = (book) => setView(book.id);
  const openNewBook = () => {
    setEditingBook({
      id: `book_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      title: '',
      coverUrl: null,
      content: '',
      pdfUrl: null,
      hidden: false,
    });
    setView('editing');
  };
  const openEditBook = (book) => {
    setEditingBook({ ...book });
    setView('editing');
  };

  const viewedBook = view && view !== 'editing' ? books.find((b) => b.id === view) : null;

  // ── Save helpers ──────────────────────────────────────────────────────────

  const commitEdit = () => {
    if (!editingBook) return;
    const existing = books.find((b) => b.id === editingBook.id);
    let updated;
    if (existing) {
      updated = { books: books.map((b) => (b.id === editingBook.id ? editingBook : b)) };
    } else {
      updated = { books: [...books, editingBook] };
    }
    onSave?.(updated);
    setView(null);
    setEditingBook(null);
  };

  const deleteBook = (bookId) => {
    const updated = { books: books.filter((b) => b.id !== bookId) };
    onSave?.(updated);
    if (view === bookId || (editingBook?.id === bookId)) {
      setView(null);
      setEditingBook(null);
    }
  };

  const toggleHidden = (bookId) => {
    const updated = {
      books: books.map((b) => (b.id === bookId ? { ...b, hidden: !b.hidden } : b)),
    };
    onSave?.(updated);
  };

  // ── Cover image upload ────────────────────────────────────────────────────

  const handleCoverFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    event.target.value = '';
    setIsUploading(true);
    setUploadError(null);
    try {
      const { uploadDataUrl } = await import('../../utils/storageUpload');
      const { resizeImage } = await import('../../utils/imageUtils');
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const resized = await resizeImage(dataUrl, 800, 800);
      const coverUrl = await uploadDataUrl(resized, 'library/covers');
      setEditingBook((prev) => ({ ...prev, coverUrl }));
    } catch (err) {
      console.error('[LibraryModal] Cover upload failed', err);
      setUploadError('Cover upload failed.');
    } finally {
      setIsUploading(false);
    }
  };

  // ── PDF upload ────────────────────────────────────────────────────────────

  const handlePdfFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    event.target.value = '';
    setIsUploading(true);
    setUploadError(null);
    try {
      const { uploadFile } = await import('../../utils/storageUpload');
      const pdfUrl = await uploadFile(file, 'library/pdfs');
      setEditingBook((prev) => ({ ...prev, pdfUrl, content: '' }));
    } catch (err) {
      console.error('[LibraryModal] PDF upload failed', err);
      setUploadError('PDF upload failed.');
    } finally {
      setIsUploading(false);
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────

  const listBg = isDark ? '#393e41' : 'var(--ivory)';
  const contentBg = isDark ? '#26292C' : 'var(--ivory)';

  return (
    <Rodal
      visible={visible}
      onClose={onClose}
      animation="slideUp"
      customStyles={{
        width: '100%',
        height: '100%',
        maxWidth: '100vw',
        maxHeight: '100vh',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        margin: 0,
        padding: 0,
        borderRadius: 0,
        background: contentBg,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      closeButton={false}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 16px',
          background: listBg,
          borderBottom: '2px solid #e94f37',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {(view !== null) && (
            <button
              onClick={() => { setView(null); setEditingBook(null); }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: isDark ? '#fff' : 'var(--jet)',
                display: 'flex',
                alignItems: 'center',
                padding: 4,
              }}
            >
              <ChevronLeft size={20} />
            </button>
          )}
          <span style={{ color: isDark ? '#fff' : 'var(--jet)', fontWeight: 700, fontSize: 16 }}>
            {view === 'editing'
              ? (editingBook && books.find((b) => b.id === editingBook.id) ? 'Edit Book' : 'New Book')
              : viewedBook
              ? viewedBook.title
              : 'Resources'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {isGm && view === null && (
            <Button
              icon={BookPlus}
              label="Add Book"
              type="cinnabar"
              disabled={isLoading}
              onPress={openNewBook}
            />
          )}
          {isGm && view === null && (
            <Button
              icon={Share2}
              label={playerShareActive ? 'Stop Sharing' : 'Share with Players'}
              type={playerShareActive ? 'blue' : 'cinnabar'}
              onPress={onTogglePlayerShare}
            />
          )}
          {isGm && view === 'editing' && (
            <Button
              label={isUploading ? 'Uploading…' : 'Save Book'}
              type="cinnabar"
              disabled={isUploading || !editingBook?.title?.trim()}
              onPress={commitEdit}
            />
          )}
          <Button icon={X} description="Close" type={isDark ? 'onyx' : 'black-white'} onPress={onClose} />
        </div>
      </div>

      {uploadError && (
        <div style={{ background: '#c0392b', color: '#fff', padding: '6px 16px', fontSize: 13 }}>
          {uploadError}
        </div>
      )}

      {/* ── List view ── */}
      {view === null && (
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            padding: 20,
            alignContent: 'flex-start',
          }}
        >
          {books.filter((b) => isGm || !b.hidden).length === 0 && (
            <div style={{ color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.45)', fontSize: 15, width: '100%', textAlign: 'center', marginTop: 40 }}>
              {isGm ? 'No books yet. Click "Add Book" to add one.' : 'The library is empty.'}
            </div>
          )}
          {books
            .filter((b) => isGm || !b.hidden)
            .map((book) => (
              <BookCard
                key={book.id}
                book={book}
                isGm={isGm}
                isDark={isDark}
                onOpen={() => openBook(book)}
                onEdit={() => openEditBook(book)}
                onToggleHidden={() => toggleHidden(book.id)}
                onDelete={() => deleteBook(book.id)}
              />
            ))}
        </div>
      )}

      {/* ── Book viewer ── */}
      {viewedBook && (
        <BookViewer book={viewedBook} isDark={isDark} />
      )}

      {/* ── Book editor (GM only) ── */}
      {view === 'editing' && editingBook && (
        <BookEditor
          book={editingBook}
          isDark={isDark}
          isUploading={isUploading}
          onChange={(updates) => setEditingBook((prev) => ({ ...prev, ...updates }))}
          onPickCover={() => coverInputRef.current?.click()}
          onPickPdf={() => pdfInputRef.current?.click()}
        />
      )}

      <Input ref={coverInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleCoverFile} />
      <Input ref={pdfInputRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={handlePdfFile} />
    </Rodal>
  );
}

// ── BookCard ─────────────────────────────────────────────────────────────────

function BookCard({ book, isGm, isDark, onOpen, onEdit, onToggleHidden, onDelete }) {
  return (
    <div
      style={{
        width: 140,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        cursor: 'pointer',
        opacity: book.hidden ? 0.5 : 1,
        position: 'relative',
      }}
    >
      {/* Cover */}
      <div
        onClick={onOpen}
        style={{
          width: 140,
          height: 190,
          borderRadius: 4,
          overflow: 'hidden',
          background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
          border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        {book.coverUrl ? (
          <img
            src={book.coverUrl}
            alt={book.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <BookOpen size={40} style={{ color: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.25)' }} />
        )}
        {book.pdfUrl && (
          <span
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              background: '#e94f37',
              color: '#fff',
              fontSize: 9,
              fontWeight: 700,
              borderRadius: 2,
              padding: '1px 4px',
            }}
          >
            PDF
          </span>
        )}
      </div>

      {/* Title */}
      <div
        onClick={onOpen}
        style={{
          color: isDark ? '#fff' : 'var(--jet)',
          fontSize: 12,
          fontWeight: 700,
          textAlign: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {book.title}
      </div>

      {/* GM actions */}
      {isGm && (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
          <button
            title={book.hidden ? 'Show to players' : 'Hide from players'}
            onClick={onToggleHidden}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: book.hidden
                ? (isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)')
                : '#e94f37',
              padding: 3,
              display: 'flex',
            }}
          >
            {book.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
          <button
            title="Edit book"
            onClick={onEdit}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
              padding: 3,
              display: 'flex',
            }}
          >
            <FileText size={14} />
          </button>
          <button
            title="Delete book"
            onClick={onDelete}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'rgba(255,100,80,0.7)',
              padding: 3,
              display: 'flex',
            }}
          >
            <Trash2 size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

// ── BookViewer ────────────────────────────────────────────────────────────────

function BookViewer({ book, isDark }) {
  if (book.pdfUrl) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <iframe
          src={book.pdfUrl}
          title={book.title}
          style={{ flex: 1, border: 'none', width: '100%', height: '100%' }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: '24px 32px',
        color: isDark ? '#e8e8e8' : 'var(--jet)',
        '--library-heading-color': isDark ? '#f0e0d0' : 'var(--jet)',
        maxWidth: 860,
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {book.coverUrl && (
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img
            src={book.coverUrl}
            alt="cover"
            style={{ maxWidth: 220, maxHeight: 300, objectFit: 'contain', borderRadius: 4 }}
          />
        </div>
      )}
      <div
        className="library_book_content"
        dangerouslySetInnerHTML={{ __html: book.content || '<em>No content.</em>' }}
        style={{ lineHeight: 1.7, fontSize: 15 }}
      />
    </div>
  );
}

// ── BookEditor ────────────────────────────────────────────────────────────────

function BookEditor({ book, isDark, isUploading, onChange, onPickCover, onPickPdf }) {
  const labelStyle = {
    color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    marginBottom: 4,
  };

  const inputStyle = {
    background: isDark ? 'rgba(255,255,255,0.07)' : 'var(--ivory)',
    border: isDark ? '1px solid rgba(255,255,255,0.18)' : '1px solid #ccc',
    borderRadius: 3,
    color: isDark ? '#fff' : 'var(--jet)',
    padding: '8px 10px',
    fontSize: 14,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };

  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: '20px 24px',
        display: 'flex',
        gap: 20,
        minHeight: 0,
      }}
    >
      {/* Left column: cover + metadata */}
      <div style={{ width: 200, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Cover preview */}
        <div>
          <div style={labelStyle}>Cover Image</div>
          <div
            onClick={onPickCover}
            style={{
              width: '100%',
              height: 250,
              background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
              border: isDark ? '1px dashed rgba(255,255,255,0.2)' : '1px dashed rgba(0,0,0,0.25)',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {book.coverUrl ? (
              <img src={book.coverUrl} alt="cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ textAlign: 'center', color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.4)', fontSize: 12 }}>
                <Upload size={24} style={{ marginBottom: 6, opacity: 0.5 }} />
                <div>Click to upload</div>
              </div>
            )}
          </div>
          {book.coverUrl && (
            <button
              onClick={() => onChange({ coverUrl: null })}
              style={{ background: 'none', border: 'none', color: 'rgba(255,100,80,0.7)', fontSize: 11, cursor: 'pointer', marginTop: 4 }}
            >
              Remove cover
            </button>
          )}
        </div>

        {/* Title */}
        <div>
          <div style={labelStyle}>Title</div>
          <Input
            style={inputStyle}
            value={book.title}
            onChange={(e) => onChange({ title: e.target.value })}
            placeholder="Book title"
          />
        </div>

        {/* Hidden toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Input
            type="checkbox"
            id="book_hidden"
            checked={!!book.hidden}
            onChange={(e) => onChange({ hidden: e.target.checked })}
            style={{ cursor: 'pointer' }}
          />
          <label htmlFor="book_hidden" style={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: 13, cursor: 'pointer' }}>
            Hide from players
          </label>
        </div>

        {/* PDF option */}
        <div>
          <div style={labelStyle}>PDF (replaces text content)</div>
          {book.pdfUrl ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ color: '#4caf50', fontSize: 12 }}>PDF uploaded ✓</span>
              <button
                onClick={() => onChange({ pdfUrl: null })}
                style={{ background: 'none', border: 'none', color: 'rgba(255,100,80,0.7)', fontSize: 11, cursor: 'pointer', textAlign: 'left', padding: 0 }}
              >
                Remove PDF
              </button>
            </div>
          ) : (
            <Button
              icon={Upload}
              label={isUploading ? 'Uploading…' : 'Upload PDF'}
              type={isDark ? 'onyx' : 'black-white'}
              disabled={isUploading}
              onPress={onPickPdf}
            />
          )}
        </div>
      </div>

      {/* Right column: content (HTML) */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
        <div style={labelStyle}>Content (HTML supported)</div>
        {book.pdfUrl ? (
          <div style={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)', fontSize: 13 }}>
            A PDF has been uploaded — remove it to use text content instead.
          </div>
        ) : (
          <TextArea
            value={book.content || ''}
            onChange={(e) => onChange({ content: e.target.value })}
            placeholder="Enter book content here. Basic HTML is supported (e.g. &lt;h2&gt;, &lt;p&gt;, &lt;img src=&quot;...&quot;&gt;, &lt;em&gt;, &lt;strong&gt;)"
            style={{
              ...inputStyle,
              flex: 1,
              resize: 'none',
              fontFamily: 'monospace',
              fontSize: 13,
              lineHeight: 1.5,
              minHeight: 300,
            }}
          />
        )}
      </div>
    </div>
  );
}
